import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Trophy, Check, X } from "lucide-react";
import { Card, CardContent } from "@/components/ui/Card.tsx";
import { Button } from "@/components/ui/Button.tsx";
import { Badge } from "@/components/ui/Badge.tsx";
import { Spinner } from "@/components/ui/Spinner.tsx";
import { showcaseApi, type ShowcaseEntry } from "@/api/showcase.ts";

type StatusFilter = "all" | "pending" | "approved" | "rejected";

function statusVariant(status: string): "warning" | "success" | "danger" | "default" {
  if (status === "pending") return "warning";
  if (status === "approved") return "success";
  if (status === "rejected") return "danger";
  return "default";
}

export function AdminShowcasePage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>("pending");

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "showcase", filter],
    queryFn: () => showcaseApi.adminList({
      status: filter === "all" ? undefined : filter,
      limit: 100,
    }),
  });

  const moderate = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "reject" }) =>
      showcaseApi.moderate(id, action),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "showcase"] }),
  });

  const entries: ShowcaseEntry[] = data?.entries ?? [];

  if (isLoading) return <div className="flex justify-center py-12"><Spinner /></div>;

  const filters: { value: StatusFilter; label: string }[] = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "all", label: "All" },
  ];

  return (
    <div className="mx-auto max-w-4xl p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-surface-400" />
          <h1 className="text-xl font-semibold text-surface-900">Showcase</h1>
        </div>
        <div className="flex items-center gap-1 bg-surface-100 rounded-lg p-0.5">
          {filters.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filter === f.value
                  ? "bg-white text-surface-900 shadow-sm"
                  : "text-surface-500 hover:text-surface-700"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {entries.length === 0 && (
          <Card><CardContent className="py-8 text-center text-surface-400 text-sm">No {filter === "all" ? "" : filter} submissions.</CardContent></Card>
        )}
        {entries.map((e) => (
          <Card key={e.id}>
            <CardContent className="py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex gap-3 min-w-0 flex-1">
                  {e.thumbnailUrl && (
                    <img
                      src={e.thumbnailUrl}
                      alt=""
                      className="w-24 h-14 rounded object-cover flex-shrink-0 bg-surface-100"
                    />
                  )}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-surface-900 truncate">{e.title || "Untitled"}</span>
                      <Badge variant={statusVariant(e.status)}>{e.status}</Badge>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-surface-500">
                      {e.channelName && <span>{e.channelName}</span>}
                      {e.niche && <span>{e.niche}</span>}
                      {e.duration && <span>{e.duration}</span>}
                      {e.viewCount !== null && <span>{e.viewCount.toLocaleString()} views</span>}
                      <span>{new Date(e.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                {e.status === "pending" && (
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moderate.mutate({ id: e.id, action: "approve" })}
                      disabled={moderate.isPending}
                      title="Approve"
                    >
                      <Check className="w-4 h-4 text-emerald-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => moderate.mutate({ id: e.id, action: "reject" })}
                      disabled={moderate.isPending}
                      title="Reject"
                    >
                      <X className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
